import { useMutation } from '@tanstack/react-query';
import React from 'react';

import { Beacon, Device, DeviceType, Gateway } from '@ixicare/resources';
import { UserAction } from '~config/toastNotification';
import { t } from '~i18n';
import NotificationsStore from '~store/Notifications';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';
import { errorStatus, humaniseErrors } from '~utils/error';

interface useDeviceArchiveProps {
  onClose?: () => void;
  onRefresh?: () => void;
  notifications: NotificationsStore;
}

export const useDeviceArchive = ({ onClose, onRefresh, notifications }: useDeviceArchiveProps) => {
  const [devicesToArchive, setDevicesToArchive] = React.useState<DBGWithType[]>([]);

  const notifyError = (message: string) =>
    notifications.addUserActionNotification({
      type: UserAction.DeviceDelete,
      notificationType: 'error',
      message,
    });

  const deleteMutation = useMutation({
    mutationFn: async (hardDelete?: boolean) => {
      if (devicesToArchive.length === 0) throw new Error('No device selected');
      const results = await Promise.allSettled(
        devicesToArchive.map((d) => deleteDevice(d.type, Number(d.id), hardDelete))
      );
      const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      return { failures, succeeded: results.length - failures.length };
    },
    // allSettled never rejects, so every failure has to be reported from here.
    onSuccess: ({ failures, succeeded }) => {
      if (failures.length) {
        // A 412 means a device still has open alarms — the only failure the user can
        // act on, so it wins over a generic message in a mixed batch.
        const hasOpenAlerts = failures.some((f) => errorStatus(f.reason) === 412);
        notifyError(
          hasOpenAlerts ? t('device.delete.openAlertsError') : humaniseErrors(failures[0].reason as Error).title
        );
      }
      // Nothing archived: keep the modal open and the selection intact so the user can retry.
      if (succeeded === 0) return;
      setDevicesToArchive([]);
      onClose?.();
      onRefresh?.();
    },
    onError: (error) => notifyError(humaniseErrors(error as Error).title),
  });

  const deleteDevice = (type: DeviceType, deviceId: number, hardDelete?: boolean) => {
    const params = {
      hardDelete: hardDelete ?? false,
    };
    if (type === DeviceType.GATEWAY) return Gateway.deleteGateway(deviceId, params);
    if (type === DeviceType.BEACON) return Beacon.deleteBeacon(deviceId, params);
    return Device.deleteDevice(deviceId, params);
  };

  return {
    devicesToArchive,
    setDevicesToArchive,
    deleteMutation,
  };
};
